import React, { FC } from 'react';
import { AOP_MAX_SECONDS } from '../../../types/AOPTypes';
import { AOPNumberField, AOPSelect, AOPSelectOption } from '../fields/AOPFields';

/**
 * A wait, written as a number and a unit rather than a count of seconds.
 *
 * Its own file because both of a decision's tabs ask for one — how long the
 * step waits before giving up, and how long an escalation waits before it
 * nudges.
 */

const UNIT_OPTIONS: AOPSelectOption[] = [
	// Seconds are offered because a plan can hold a wait that is not a whole
	// number of minutes — the compiler's own prompt has a one-second timeout —
	// and rounding one to the nearest minute meant the field disagreed with the
	// document, then saved its own version over it on the next edit.
	{ value: '1', label: 'seconds' },
	{ value: '60', label: 'minutes' },
	{ value: '3600', label: 'hours' },
	{ value: '86400', label: 'days' },
];

/** Split seconds into the largest whole unit, so 2700 edits as 45 minutes. */
const splitSeconds = (seconds: number): { value: number; unit: string } => {
	if (seconds % 86400 === 0) {
		return { value: seconds / 86400, unit: '86400' };
	}
	if (seconds % 3600 === 0) {
		return { value: seconds / 3600, unit: '3600' };
	}
	if (seconds % 60 === 0) {
		return { value: seconds / 60, unit: '60' };
	}

	// Anything else is shown as it is stored rather than rounded to a minute.
	return { value: seconds, unit: '1' };
};

/** A duration as a number and a unit, committed together. */
export const AOPDurationField: FC<{
	seconds: number;
	onCommit: (seconds: number) => void;
	disabled?: boolean;
	ariaLabel: string;
}> = ({ seconds, onCommit, disabled, ariaLabel }) => {
	const split = splitSeconds(seconds);

	return (
		<div className="flex items-center gap-xs">
			{/*
			  Capped by the unit, so eight days cannot be typed at all.

			  A wait over the service's seven-day limit used to be accepted here and
			  refused on save, leaving the field showing a number that was never
			  stored. The limit is one duration, so the ceiling moves with the unit:
			  7 days, 168 hours, 10080 minutes.
			*/}
			<AOPNumberField
				ariaLabel={ariaLabel}
				value={split.value}
				max={Math.floor(AOP_MAX_SECONDS / parseInt(split.unit, 10))}
				disabled={disabled}
				onCommit={(value) => onCommit(value * parseInt(split.unit, 10))}
			/>
			<AOPSelect
				ariaLabel={`${ariaLabel} unit`}
				value={split.unit}
				options={UNIT_OPTIONS}
				disabled={disabled}
				// Changing the unit can take the same number past the limit — 8 minutes
				// becoming 8 days — so the value is clamped as the unit changes.
				onChange={(unit) => onCommit(Math.min(split.value * parseInt(unit, 10), AOP_MAX_SECONDS))}
				className="w-[140px]"
			/>
		</div>
	);
};
